import { prompt } from './prompts';
import { sendGetRequest, sendPostRequest } from './utils';

const RETRY_SECONDS = 60;

function formatCountdown(seconds) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

function showSessionExpiredPrompt() {
    if (!document.querySelector('[data-session-expired]')) return;

    prompt({
        title: gettext('UWAGA!'),
        html: `<div class="text-center">${gettext('Twoja sesja wygasła')}</div>`,
        confirmText: gettext('OK'),
        showCancelButton: false,
    });
}

/**
 * Warns the user before they are logged out for inactivity,
 * with a countdown and a button which extends the session.
 *
 * The time left and the warning length (both set in the site configuration)
 * are always read from the server, so requests made in the
 * meantime (also in other tabs) postpone the warning.
 */
export function initializeSessionTimeout() {
    showSessionExpiredPrompt();

    const alerts = document.querySelector('.alerts');
    if (!document.body.dataset.username || !alerts) return;

    let checkTimeoutId = null;
    let countdownIntervalId = null;
    let warning = null;

    function scheduleCheck(seconds) {
        clearTimeout(checkTimeoutId);
        checkTimeoutId = setTimeout(checkSession, seconds * 1000);
    }

    function hideWarning() {
        clearInterval(countdownIntervalId);
        warning?.remove();
        warning = null;
    }

    function showWarning(secondsLeft) {
        if (!warning) {
            // Kept above the regular alerts, which are appended below it.
            alerts.insertAdjacentHTML(
                'afterbegin',
                `
                <div class="alert session-timeout">
                    <span class="tag">${gettext('UWAGA!')}</span>
                    <span class="text">${gettext('Twoja sesja wygasa za:')}</span>
                    <span class="countdown"></span>
                    <button type="button" class="btn-submit">${gettext('Odśwież')}</button>
                </div>
            `,
            );
            warning = alerts.firstElementChild;
            warning
                .querySelector('button')
                .addEventListener('click', refreshSession);
            requestAnimationFrame(() => warning?.classList.add('show'));
        }

        const countdown = warning.querySelector('.countdown');
        const deadline = Date.now() + secondsLeft * 1000;
        const updateCountdown = () => {
            const seconds = Math.max(
                0,
                Math.ceil((deadline - Date.now()) / 1000),
            );
            countdown.textContent = formatCountdown(seconds);
        };
        clearInterval(countdownIntervalId);
        updateCountdown();
        countdownIntervalId = setInterval(updateCountdown, 1000);
    }

    async function checkSession() {
        let secondsLeft;
        let warningSeconds;
        try {
            // An expired session responds with 401, which redirects to the login page.
            const response = await sendGetRequest('/api/users/session/');
            ({ seconds_left: secondsLeft, warning_seconds: warningSeconds } =
                await response.json());
        } catch (error) {
            console.error('Error checking session:', error);
            scheduleCheck(RETRY_SECONDS);
            return;
        }
        if (secondsLeft === null) {
            hideWarning();
            return;
        }

        if (secondsLeft > warningSeconds) {
            hideWarning();
            scheduleCheck(secondsLeft - warningSeconds);
        } else {
            showWarning(Math.floor(secondsLeft));
            // Check again right after the deadline, when the server logs the user out.
            scheduleCheck(secondsLeft + 1);
        }
    }

    async function refreshSession() {
        try {
            await sendPostRequest('/api/users/session/refresh/');
        } catch (error) {
            console.error('Error refreshing session:', error);
        }
        checkSession();
    }

    checkSession();
}
